import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/MailingListForm.css";
import { db, collection, addDoc } from "../firebase";
import FaqSection from "./parts/FaqSection";

const MailingListForm = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        marketingAcceptance: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Check if user has already submitted the form
        const hasSubmittedForm = localStorage.getItem("brandBounceFormSubmitted");

        if (hasSubmittedForm) {
            // Redirect to resources page if already submitted
            navigate("/resources");
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const validate = () => {
        let formErrors = {};
        if (!formData.firstName) formErrors.firstName = "First name is required.";
        if (!formData.lastName) formErrors.lastName = "Last name is required.";
        if (!formData.email) formErrors.email = "Email is required.";
        if (!formData.email.match(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/))
            formErrors.email = "Invalid email format.";
        if (!formData.marketingAcceptance)
            formErrors.marketingAcceptance = "You must accept marketing communications.";
        return formErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            setIsLoading(true);
            try {
                // Store the data in Firebase
                await addDoc(collection(db, "mailingList"), {
                    ...formData,
                    timestamp: new Date()
                });

                // Store in localStorage that the user has submitted the form
                localStorage.setItem("brandBounceFormSubmitted", "true");
                localStorage.setItem("brandBounceUserEmail", formData.email);

                // Redirect to resources page
                navigate("/resources");

                // Reset form (not strictly necessary since we're redirecting)
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    marketingAcceptance: false
                });
            } catch (error) {
                console.error("Error submitting form: ", error);
                alert("There was an error. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        } else {
            setErrors(validationErrors);
        }
    };


    return (
        <div className="mailing-list-container">
            <h1 className="mailing-list-title">Get Access to Our Resources</h1>
            <p className="mailing-list-description">
                Fill out the form below to join our mailing list and get access to exclusive resources.
            </p>

            <form className="mailing-list-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label>First Name</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                        {errors.firstName && <span className="error">{errors.firstName}</span>}
                    </div>

                    <div className="form-group">
                        <label>Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                        {errors.lastName && <span className="error">{errors.lastName}</span>}
                    </div>
                </div>

                <div className="form-group full-width">
                    <label>Email</label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </div>

                <div className="form-group checkbox-group">
                    <input
                        type="checkbox"
                        name="marketingAcceptance"
                        checked={formData.marketingAcceptance}
                        onChange={handleChange}
                        id="marketingAcceptance"
                    />
                    <label htmlFor="marketingAcceptance">
                        I agree to receive marketing communications from Brand Bounce
                    </label>
                    {errors.marketingAcceptance && (
                        <span className="error">{errors.marketingAcceptance}</span>
                    )}
                </div>

                <div className="form-group submit-group">
                    <button type="submit" className="cta-button" disabled={isLoading}>
                        {isLoading ? 'Submitting...' : 'Get Resources'}
                    </button>
                </div>
            </form>

            {/* Loading overlay */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loader-container">
                        <div className="loader"></div>
                        <p>Submitting your information...</p>
                    </div>
                </div>
            )}

            <FaqSection />
        </div>
    );
};

export default MailingListForm;
